import { vars } from '../../theme';
import React, { useEffect, useRef, useState } from 'react';
import { isBreakingLine } from '../HBBanner/utils';
import {
    BannerContainer,
    Close,
    Container,
    Content,
    IconContainer,
    StyledTopRow,
    TextContainer
} from '../HBBanner/HBBanner.styles';
import { HBBannerBaseProps } from '../HBBanner/HBBanner.types';
import { useValidateBanner } from '../HBBanner/useValidateBanner';
import { useWindowSize } from '../../hooks/useWindowSize';
import { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import HBBannerActions from './BannerActions';
import HBText from '../HBText/HBText';

const HBBannerBase = (
    props: HBBannerBaseProps & MustIncludeOneOfBasicTestIdProps
) => {
    const {
        className,
        testIdName,
        testId,
        title,
        content,
        primaryAction,
        secondaryAction,
        primaryActionTheme = 'primary',
        width,
        hideIcon,
        onClose,
        topPadding,
        margin,
        whiteBanner = false,
        accentLine,
        background,
        icon,
        setActionsInline,
        actionsTheme = 'primary',
        zIndex,
        gradientBorderWidth,
        titleSuffix
    } = props;

    const [isMultiLineDesign, setIsMultiLineDesign] = useState(null);
    const containerRef = useRef(null);
    const textContainerRef = useRef(null);
    const { isMediumUp } = useWindowSize();
    useValidateBanner({ actionsTheme, primaryAction, secondaryAction });

    const hasIcon = icon && !hideIcon;

    useEffect(() => {
        function calculateLineBreak() {
            if (setActionsInline === undefined) {
                if (textContainerRef.current) {
                    if (title || titleSuffix) {
                        setIsMultiLineDesign(true);
                    } else {
                        setIsMultiLineDesign(
                            isBreakingLine(content, textContainerRef.current)
                        );
                    }
                }
            } else {
                setIsMultiLineDesign(!setActionsInline);
            }
        }
        calculateLineBreak();

        window.addEventListener('resize', calculateLineBreak);
        return () => {
            window.removeEventListener('resize', calculateLineBreak);
        };
    }, [content, setActionsInline, title, titleSuffix]);

    const BannerIcon = () => {
        const Icon = icon;

        return (
            <IconContainer>
                <Icon useDefaultFill />
            </IconContainer>
        );
    };

    const enableCloseButton = !!onClose;

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'banner',
        componentName: 'HBBannerBase'
    });

    return (
        <BannerContainer
            className={className}
            ref={containerRef}
            hasIcon={hasIcon}
            width={width}
            margin={margin}
            topPadding={topPadding}
            whiteBanner={whiteBanner}
            accentLine={accentLine}
            background={background}
            isMultiLineDesign={isMultiLineDesign}
            enableCloseButton={enableCloseButton}
            data-testid={finalTestId}
            zIndex={zIndex}
            gradientBorderWidth={gradientBorderWidth}
        >
            {enableCloseButton && (
                <Close
                    data-testid="hb-banner-close--button"
                    color={vars.colors.text.secondary}
                    onClick={onClose}
                    $isMultiLineDesign={isMultiLineDesign}
                />
            )}
            <Container
                paddingLeft={accentLine.includes('gradient') ? '4px' : '0'} //add padding if the accent line is a gradient because the gradient is not part of the border
            >
                {hasIcon && <BannerIcon />}
                <TextContainer>
                    {(title || titleSuffix) && (
                        <StyledTopRow>
                            {title && (
                                <HBText type="emphasis16" color="text.primary">
                                    {title}
                                </HBText>
                            )}
                            {titleSuffix && titleSuffix}
                        </StyledTopRow>
                    )}

                    <Content
                        type="body14"
                        color="text.primary"
                        ref={textContainerRef}
                    >
                        {content}
                    </Content>
                </TextContainer>
            </Container>

            <HBBannerActions
                align="end"
                testId={finalTestId}
                actionsTheme={actionsTheme}
                actionsSize={isMediumUp ? 'xs' : 'md'}
                primaryAction={primaryAction}
                primaryActionTheme={primaryActionTheme}
                secondaryAction={secondaryAction}
            />
        </BannerContainer>
    );
};

export default HBBannerBase;
